import { z } from 'zod';
/**
 * Shared primitives of the MeshAlert API contract.
 *
 * This package is the single source of truth for the HTTP surface: the API
 * validates every request against these schemas, the web client derives its
 * types from them. Changing an endpoint means changing this file in the same
 * commit as its callers.
 */
export const API_VERSION = 'v1';
export const uuid = z.uuid();
/** UTC ISO-8601 with explicit offset, as produced by `Date#toISOString()`. */
export const isoTimestamp = z.iso.datetime({ offset: true });
/** Machine-readable error codes. Clients branch on these, never on prose. */
export const ERROR_CODES = [
    'bad_request',
    'validation_failed',
    'unauthenticated',
    'forbidden',
    'not_found',
    'conflict',
    'unprocessable',
    'rate_limited',
    'internal_error',
    'dependency_unavailable',
    'payload_too_large',
];
export const errorCode = z.enum(ERROR_CODES);
/** The one error envelope every non-2xx response uses. */
export const errorResponse = z.object({
    error: z.object({
        code: errorCode,
        message: z.string(),
        details: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
        request_id: z.string().optional(),
    }),
});
export const HTTP_STATUS_BY_ERROR_CODE = {
    bad_request: 400,
    validation_failed: 422,
    unauthenticated: 401,
    forbidden: 403,
    not_found: 404,
    conflict: 409,
    unprocessable: 422,
    rate_limited: 429,
    internal_error: 500,
    dependency_unavailable: 503,
    payload_too_large: 413,
};
/** Cursor pagination. Offsets skip and duplicate rows under concurrent writes. */
export const pageQuery = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().min(1).optional(),
});
export function pageOf(item) {
    return z.object({
        items: z.array(item),
        next_cursor: z.string().nullable(),
    });
}
/** Free-form key/value labels carried from the alert source. */
export const labels = z.record(z.string().min(1).max(128), z.string().max(1024));
export const SEVERITIES = ['info', 'warning', 'error', 'critical'];
export const severity = z.enum(SEVERITIES);
/** Higher rank = more urgent. Used by route matching (`severity_min`). */
export const SEVERITY_RANK = {
    info: 0,
    warning: 1,
    error: 2,
    critical: 3,
};
export const ALERT_STATUSES = ['firing', 'acknowledged', 'resolved'];
export const alertStatus = z.enum(ALERT_STATUSES);
export const USER_ROLES = ['admin', 'operator', 'viewer'];
export const userRole = z.enum(USER_ROLES);
/**
 * Woher ein Alarm kommt.
 *
 * `generic`, `alertmanager` und `grafana` liefern per Webhook an. `monitor`
 * prüft MeshAlert selbst nach Zeitplan, `agent` ist ein Dienst auf einem
 * fremden Server, der von innen meldet, was von aussen nicht sichtbar ist.
 */
export const SOURCE_TYPES = ['generic', 'alertmanager', 'grafana', 'monitor', 'agent'];
export const sourceType = z.enum(SOURCE_TYPES);
/**
 * A channel is one delivery endpoint owned by one user: a phone, a Mac, or an
 * outbound webhook. "Sender node" in product language.
 */
export const CHANNEL_KINDS = ['apns_ios', 'apns_macos', 'webhook'];
export const channelKind = z.enum(CHANNEL_KINDS);
export const DELIVERY_STATUSES = ['pending', 'sending', 'delivered', 'failed', 'dead'];
export const deliveryStatus = z.enum(DELIVERY_STATUSES);
//# sourceMappingURL=primitives.js.map